import { CONFIGURATION } from "@revolt/common";

import { PROCESSOR, WORKLET_CODE } from "./worklet";

/**
 * Konkord: where the DPDFNet2 model and onnxruntime-web's runtime come from
 */
export function dpdfnetAssets() {
  const configured = CONFIGURATION.DPDFNET_ASSETS_URL;
  // an empty setting means "not set" (replacing on it would give "/")
  const base = configured ? configured.replace(/\/*$/, "/") : undefined;
  // onnxruntime-web's glue and binary must match the bundled version (1.22.0)
  const runtime =
    base ?? "https://cdn.jsdelivr.net/npm/onnxruntime-web@1.22.0/dist/";
  return {
    model: base
      ? `${base}dpdfnet2_48khz_hr.onnx`
      : "https://huggingface.co/Ceva-IP/DPDFNet/resolve/main/onnx/dpdfnet2_48khz_hr.onnx",
    mjs: `${runtime}ort-wasm-simd-threaded.mjs`,
    wasm: `${runtime}ort-wasm-simd-threaded.wasm`,
  };
}

/**
 * Start downloading the model and runtime early (e.g. when joining a call),
 * so the worker finds them in the HTTP cache. The bodies are read to the end,
 * an unread response may not be cached whole.
 */
export function preloadDpdfnet() {
  const { model, mjs, wasm } = dpdfnetAssets();
  for (const url of [model, mjs, wasm]) {
    fetch(url)
      .then((response) => response.arrayBuffer())
      .catch(() => {});
  }
}

/** building the session after the download; the download itself can take long */
const BUILD_TIMEOUT_MS = 30_000;
const DOWNLOAD_TIMEOUT_MS = 5 * 60_000;

const modules = new WeakMap<BaseAudioContext, Promise<void>>();

function loadWorklet(context: AudioContext) {
  let module = modules.get(context);
  if (!module) {
    const url = URL.createObjectURL(
      new Blob([WORKLET_CODE], { type: "application/javascript" }),
    );
    module = context.audioWorklet
      .addModule(url)
      .finally(() => URL.revokeObjectURL(url));
    modules.set(context, module);
  }
  return module;
}

/**
 * Konkord: DPDFNet2 noise suppression node. Resolves once the model is loaded;
 * onFail is called if this computer can't keep up or the worker dies / stalls
 * (switch to a lighter engine)
 */
export async function createDpdfnetNode(
  context: AudioContext,
  onFail: (reason: string) => void,
): Promise<{ node: AudioWorkletNode; dispose: () => void }> {
  await loadWorklet(context);

  const worker = new Worker(new URL("./worker.ts", import.meta.url), {
    type: "module",
  });
  const channel = new MessageChannel();

  try {
    await new Promise<void>((resolve, reject) => {
      let timeout = setTimeout(
        () => reject(new Error("DPDFNet2 download took too long")),
        DOWNLOAD_TIMEOUT_MS,
      );
      worker.onmessage = (event) => {
        const type = event.data?.type;
        if (type === "downloaded") {
          clearTimeout(timeout);
          timeout = setTimeout(
            () => reject(new Error("DPDFNet2 took too long to start")),
            BUILD_TIMEOUT_MS,
          );
        } else if (type === "ready") {
          clearTimeout(timeout);
          resolve();
        } else if (type === "error") {
          clearTimeout(timeout);
          reject(new Error(event.data.message));
        }
      };
      worker.onerror = (event) => {
        clearTimeout(timeout);
        reject(new Error(event.message || "DPDFNet2 worker failed"));
      };
      worker.postMessage(
        { type: "init", ...dpdfnetAssets(), port: channel.port1 },
        [channel.port1],
      );
    });

    const node = new AudioWorkletNode(context, PROCESSOR, {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      outputChannelCount: [1],
    });

    let failed = false;
    const fail = (reason: string) => {
      if (failed) return;
      failed = true;
      console.warn(`[VoiceProcessor] DPDFNet2 off: ${reason}`);
      onFail(reason);
    };
    worker.onmessage = (event) => {
      if (event.data?.type === "slow") {
        fail(
          `too slow here (${Number(event.data.average).toFixed(1)} ms per 10 ms)`,
        );
      } else if (event.data?.type === "error") {
        fail(`failed: ${event.data.message}`);
      }
    };
    // after starting, a dead worker must not leave the mic silent
    worker.onerror = (event) => fail(`worker died: ${event.message}`);
    node.port.onmessage = (event) => {
      if (event.data?.stalled)
        fail(
          event.data.choppy
            ? "voice breaking up (worker late again and again)"
            : "worker stopped answering",
        );
    };
    node.port.postMessage({ port: channel.port2 }, [channel.port2]);

    return {
      node,
      dispose: () => {
        node.port.postMessage({ stop: true });
        node.disconnect();
        worker.terminate();
      },
    };
  } catch (err) {
    worker.terminate();
    throw err;
  }
}
